import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscriber, Subscription, firstValueFrom, Subject, from } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { map, take, switchMap } from 'rxjs/operators';
import { EnvironmentService } from '../shared/environment.service';
import { FirebaseAuthService } from '../shared/firebase-auth.service';
import {
  SendMessageRequest,
  toChatAttachment,
  ChatMessageResponse,
  ChatModelOption,
  CreateSessionRequest,
  CreateSessionResponse,
  GetSessionHistoryResponse,
  ListSessionsResponse,
} from '../models/chat.models';

/**
 * Service for chat API communication.
 *
 * Handles:
 * - Session management (create, list, get history, delete) via REST
 * - Persistent WebSocket connection with pre-connect for low-latency streaming (Issue #714)
 * - Future: Gemini Live audio streaming support
 */
@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private auth = inject(FirebaseAuthService);

  private readonly API_BASE: string;
  private readonly WS_BASE: string;

  // Persistent WebSocket state (Issue #714: pre-connect to reduce TTFT)
  private ws$?: WebSocketSubject<any>;
  private wsSubscription?: Subscription;
  private connectedSessionId?: string;
  private connectionReady?: Promise<void>;
  private connectionReadyResolve?: () => void;
  private connectionReadyReject?: (reason?: unknown) => void;
  private activeMessageObserver?: Subscriber<ChatMessageResponse>;
  private wsGeneration = 0; // guards stale close events from clobbering newer connections

  /**
   * Issue #733: WebSocket keepalive, auto-reconnection, and idle timeout.
   *
   * **Problem — connection drops:**
   * The chat WebSocket is routed through Cloud Run's load balancer, which
   * silently drops idle TCP connections after ~300s of inactivity. The browser
   * sees a CloseEvent with code 1006 (abnormal closure), wasClean=false.
   *
   * **Problem — cost of idle connections:**
   * An open WebSocket holds a Cloud Run instance alive — it cannot scale to
   * zero. Without an idle timeout, a single forgotten browser tab keeps an
   * instance running (and billing) indefinitely.
   *
   * **Solution — three cooperating mechanisms:**
   *
   * 1. KEEPALIVE (30s ping): Sends `{ type: 'ping' }` to reset the load
   *    balancer's idle timer. The server ignores these messages. This prevents
   *    drops during *active* use.
   *
   * 2. IDLE TIMEOUT (5 min foreground / 1 min hidden tab): Tracks user
   *    activity via {@link notifyActivity} (called by ChatComponent on mouse,
   *    keyboard, and touch events). When no activity is detected for the
   *    timeout period, the WebSocket is disconnected to allow Cloud Run to
   *    scale to zero. The idle check piggybacks on the keepalive timer — no
   *    additional timer is needed. Streams in progress are never interrupted.
   *
   * 3. RECONNECT (linear backoff, max 3 attempts): On unexpected closure
   *    (codes 1006/1001/1005), retries at 2s, 4s, 6s. Clean closures
   *    (user-initiated disconnect, idle timeout) do not trigger reconnection.
   *    When the user returns after idle disconnect, {@link sendMessage} and
   *    {@link preConnect} transparently re-establish the connection.
   *    sendMessage also retries once on failure for additional resilience.
   */
  private static readonly KEEPALIVE_INTERVAL_MS = 30_000;
  private static readonly RECONNECT_DELAY_MS = 2_000;
  private static readonly MAX_RECONNECT_ATTEMPTS = 3;
  private static readonly IDLE_TIMEOUT_MS = 5 * 60_000;
  private static readonly IDLE_TIMEOUT_HIDDEN_MS = 60_000;
  private keepaliveTimer?: ReturnType<typeof setInterval>;
  private reconnectAttempts = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private manualDisconnect = false;
  private lastActivityTime = Date.now();
  private tabHidden = false;

  // Issue #1004: Async decoration updates delivered after isFinal
  private messageUpdateSubject = new Subject<ChatMessageResponse>();
  readonly messageUpdate$ = this.messageUpdateSubject.asObservable();

  // Navigation trigger for UI tools (Issue #292)
  private navigationTrigger$ = new Subject<string>();

  /**
   * Issue #933: Emits after a successful auto-reconnect following a mid-stream
   * WebSocket disconnection. Subscribers (e.g. ChatComponent) can use this to
   * automatically retry the interrupted message.
   */
  private reconnectedAfterInterrupt$ = new Subject<void>();
  private hadActiveStreamOnDisconnect = false;

  /**
   * Observable for UI navigation requests from agent tools.
   * Subscribe to this to handle ShowObjectOnPage tool calls.
   */
  get navigationRequested$(): Observable<string> {
    return this.navigationTrigger$.asObservable();
  }

  /**
   * Observable that emits when the WebSocket successfully reconnects after
   * an interruption that occurred while a message was being streamed.
   */
  get reconnectedWithPendingRetry$(): Observable<void> {
    return this.reconnectedAfterInterrupt$.asObservable();
  }

  constructor() {
    this.API_BASE = `https://${this.env.getApiServer()}/v1/chat`;

    // WebSocket endpoint (Issue #270: Separate Cloud Run service)
    const wsServer = this.env.getWsServer();

    if (!wsServer || wsServer.includes('localhost')) {
      this.WS_BASE = `ws://localhost:8081`;
    } else {
      this.WS_BASE = `wss://${wsServer}`;
    }

    ClientLoggingService.log('INFO', '[ChatService] API endpoint:', this.API_BASE);
    ClientLoggingService.log('INFO', '[ChatService] WebSocket endpoint:', this.WS_BASE);
  }

  /**
   * Create a new chat session.
   */
  createSession(request: CreateSessionRequest): Observable<CreateSessionResponse> {
    return this.http.post<CreateSessionResponse>(`${this.API_BASE}/sessions`, request);
  }

  /**
   * Get session history with messages.
   */
  getSessionHistory(sessionId: string, limit: number = 50, cursor?: string): Observable<GetSessionHistoryResponse> {
    const params: any = { limit };
    if (cursor) params.cursor = cursor;

    return this.http.get<GetSessionHistoryResponse>(`${this.API_BASE}/sessions/${sessionId}`, { params }).pipe(
      map(response => {
        // Process messages to ensure they match UI expectations
        // 1. Parse tool call JSON strings (parametersJson -> parameters)
        // 2. Hydrate timeline from legacy metadata (thinkingContent/toolCalls -> timeline)
        if (response.messages) {
          ClientLoggingService.log(
            'INFO',
            '[ChatService] getSessionHistory raw messages:',
            response.messages.length,
            response.messages
          );
          response.messages.forEach(msg => {
            // Attachments arrive in their wire shape: the state is the protobuf enum name
            // (ATTACHMENT_READY) and byteSize is a string, because a 64-bit integer cannot survive
            // JSON as a number. The rest of the UI works in the mapped shape, and one gate in
            // particular — the thumbnail strip only requests a signed URL for an attachment whose
            // state is 'ready' — silently skips anything still carrying the enum. That is why a
            // restored message showed placeholder glyphs and never fetched an image at all.
            if (msg.attachments?.length) {
              msg.attachments = msg.attachments.map(raw => toChatAttachment(raw as never));
            }

            if (msg.metadata) {
              // Parse tool call JSONs
              if (msg.metadata.toolCalls) {
                msg.metadata.toolCalls.forEach((tc: any) => {
                  if (tc.parametersJson && !tc.parameters) {
                    try {
                      tc.parameters = JSON.parse(tc.parametersJson);
                    } catch (e) {
                      ClientLoggingService.log('WARN', '[ChatService] Failed to parse tool call parameters:', e);
                      tc.parameters = {};
                    }
                  }
                  if (tc.resultJson && !tc.result) {
                    try {
                      tc.result = JSON.parse(tc.resultJson);
                    } catch (e) {
                      ClientLoggingService.log('WARN', '[ChatService] Failed to parse tool call result:', e);
                      tc.result = tc.resultJson;
                    }
                  }
                });
              }

              // Fix missing 'type' and normalize casing in timeline events
              if (msg.metadata.timeline) {
                msg.metadata.timeline.forEach((event: any) => {
                  if (!event.type) {
                    // Handle missing type (Enum 0 = THINKING or UNSPECIFIED)
                    if (event.thinkingContent) {
                      event.type = 'thinking';
                    } else if (event.toolCall) {
                      event.type = 'tool_call';
                    }
                  } else {
                    // Normalize uppercase from Proto JSON (THINKING -> thinking)
                    event.type = event.type.toLowerCase();
                  }

                  // Map backend 'thinkingContent' to frontend 'content'
                  if (event.thinkingContent && !event.content) {
                    event.content = event.thinkingContent;
                  }

                  // Map backend 'timestampMs' to frontend 'timestamp'
                  if (event.timestampMs != null && event.timestamp == null) {
                    event.timestamp = event.timestampMs;
                  }

                  // Parse tool call JSON fields within timeline events
                  if (event.toolCall) {
                    if (event.toolCall.parametersJson && !event.toolCall.parameters) {
                      try {
                        event.toolCall.parameters = JSON.parse(event.toolCall.parametersJson);
                      } catch {
                        event.toolCall.parameters = {};
                      }
                    }
                    if (event.toolCall.resultJson && !event.toolCall.result) {
                      try {
                        event.toolCall.result = JSON.parse(event.toolCall.resultJson);
                      } catch {
                        event.toolCall.result = event.toolCall.resultJson;
                      }
                    }
                  }
                });
              }

              // Hydrate timeline if missing
              if (!msg.metadata.timeline || msg.metadata.timeline.length === 0) {
                const timeline: any[] = [];

                // Add thinking event
                if (msg.metadata.thinkingContent) {
                  timeline.push({
                    type: 'thinking',
                    content: msg.metadata.thinkingContent,
                    timestamp: 0,
                  });
                }

                // Add tool call events
                if (msg.metadata.toolCalls) {
                  msg.metadata.toolCalls.forEach(tc => {
                    timeline.push({
                      type: 'tool_call',
                      toolCall: tc,
                      timestamp: 0,
                    });
                  });
                }

                if (timeline.length > 0) {
                  msg.metadata.timeline = timeline;
                }
              }
            }
          });
        }
        return response;
      })
    );
  }

  /**
   * List user's chat sessions.
   */
  listSessions(projectId?: string, limit: number = 20): Observable<ListSessionsResponse> {
    const params: any = { limit };
    if (projectId) params.projectId = projectId;

    return this.http.get<ListSessionsResponse>(`${this.API_BASE}/sessions`, { params });
  }

  /**
   * List available AI models for the chat selector (Issue #706).
   * Calls the platform-level ModelService (GET /v1/models), not the chat API.
   * The backend Model enum is the single source of truth.
   */
  listModels(): Observable<ChatModelOption[]> {
    const modelsUrl = `https://${this.env.getApiServer()}/v1/models`;
    return this.http.get<{ models: ChatModelOption[] }>(modelsUrl).pipe(map(response => response.models || []));
  }

  /**
   * Pre-establish WebSocket connection for a session (Issue #714).
   * Call when a session is loaded to eliminate connection latency on the first message.
   * Returns a promise that resolves when the connection is open and ready.
   */
  async preConnect(sessionId: string): Promise<void> {
    if (this.connectedSessionId === sessionId && this.ws$) {
      return this.connectionReady;
    }

    this.disconnect();

    const user = await firstValueFrom(this.auth.user$);
    if (!user) {
      throw new Error('No authenticated user — cannot establish WebSocket');
    }

    const token = await user.getIdToken();
    const wsUrl = `${this.WS_BASE}/ws/chat?session_id=${sessionId}`;

    ClientLoggingService.log('INFO', '[ChatService] Pre-connecting WebSocket for session:', sessionId);
    const connectStart = Date.now();
    const generation = ++this.wsGeneration;

    this.connectionReady = new Promise<void>((resolve, reject) => {
      this.connectionReadyResolve = resolve;
      this.connectionReadyReject = reject;
    });

    this.ws$ = webSocket<any>({
      url: wsUrl,
      openObserver: {
        next: () => {
          if (this.wsGeneration !== generation) return;
          ClientLoggingService.log('INFO', '[ChatService] WebSocket open, sending auth...');
          this.ws$?.next({ type: 'auth', token });
        },
      },
      closeObserver: {
        next: (event: CloseEvent) => {
          if (this.wsGeneration !== generation) return;
          this.stopKeepalive();
          const code = event.code;
          ClientLoggingService.log(
            'INFO',
            `[ChatService] WebSocket disconnected (code=${code}, clean=${event.wasClean})`
          );

          this.connectedSessionId = undefined;
          this.ws$ = undefined;
          this.connectionReadyReject?.(new Error('WebSocket closed before ready'));
          this.connectionReadyReject = undefined;

          // Issue #933: Track whether the disconnect interrupted an active stream
          // so we can auto-retry after reconnection.
          this.hadActiveStreamOnDisconnect = !!this.activeMessageObserver;

          // Issue #854: Signal any in-flight message stream so the UI exits the
          // "thinking" / "streaming" state instead of hanging indefinitely.
          // The error handler and complete handler already do this, but
          // closeObserver fires for CloseEvent (e.g., network drop, Cloud Run
          // idle timeout) which bypasses those paths.
          if (this.activeMessageObserver) {
            this.activeMessageObserver.error(ChatService.toError(event));
            this.activeMessageObserver = undefined;
          }

          // Reconnect on:
          //  - 1006 (abnormal closure, e.g. network drop)
          //  - 1001 (going away / idle timeout from Jetty)
          //  - 1005 (no status received, proxied idle timeout)
          const reconnectable = [1006, 1001, 1005];
          if (
            !this.manualDisconnect &&
            reconnectable.includes(code) &&
            this.reconnectAttempts < ChatService.MAX_RECONNECT_ATTEMPTS
          ) {
            this.scheduleReconnect(sessionId);
          }
        },
      },
    });

    this.wsSubscription = this.ws$.subscribe({
      next: data => {
        if (data?.type === 'auth_ok') {
          if (this.wsGeneration !== generation) return;
          const elapsed = Date.now() - connectStart;
          ClientLoggingService.log('INFO', `[ChatService] WebSocket authenticated (${elapsed}ms)`);
          this.connectedSessionId = sessionId;
          this.reconnectAttempts = 0;
          this.startKeepalive();
          this.connectionReadyResolve?.();
          return;
        }

        const chunk = this.parseChunk(data);

        // Issue #1004: MESSAGE_UPDATE arrives after isFinal (async decoration).
        // Route directly to subscribers without touching activeMessageObserver.
        if (chunk.type === 'MESSAGE_UPDATE' && chunk.messageId) {
          this.messageUpdateSubject.next(chunk);
          return;
        }

        if (this.activeMessageObserver) {
          this.activeMessageObserver.next(chunk);
          if (chunk.isFinal) {
            ClientLoggingService.log('INFO', '[ChatService] Final chunk received, completing message stream');
            this.activeMessageObserver.complete();
            this.activeMessageObserver = undefined;
          }
        }
      },
      error: err => {
        if (this.wsGeneration !== generation) return;
        this.stopKeepalive();
        ClientLoggingService.log('ERROR', '[ChatService] WebSocket error:', err);
        this.connectedSessionId = undefined;
        this.ws$ = undefined;
        this.connectionReadyReject?.(ChatService.toError(err));
        this.connectionReadyReject = undefined;
        if (this.activeMessageObserver) {
          // Issue #854: RxJS passes raw CloseEvent objects on abnormal WS close.
          // CloseEvent.toString() produces the unhelpful "[object CloseEvent]".
          // Wrap in a proper Error with a user-friendly message.
          const wrapped = ChatService.toError(err);
          this.activeMessageObserver.error(wrapped);
          this.activeMessageObserver = undefined;
        }
      },
      complete: () => {
        if (this.wsGeneration !== generation) return;
        this.stopKeepalive();
        ClientLoggingService.log('INFO', '[ChatService] WebSocket stream completed');
        this.connectedSessionId = undefined;
        this.ws$ = undefined;
        if (this.activeMessageObserver) {
          this.activeMessageObserver.complete();
          this.activeMessageObserver = undefined;
        }
      },
    });

    return this.connectionReady;
  }

  /**
   * Close the persistent WebSocket connection.
   * Call when switching sessions or destroying the chat component.
   */
  disconnect(): void {
    this.manualDisconnect = true;
    this.stopKeepalive();
    this.cancelPendingReconnect();
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    this.ws$?.complete();
    this.ws$ = undefined;
    this.connectedSessionId = undefined;
    this.activeMessageObserver = undefined;
    this.connectionReady = undefined;
    this.connectionReadyResolve = undefined;
    this.connectionReadyReject = undefined;
    this.reconnectAttempts = 0;
    this.manualDisconnect = false;
  }

  /**
   * Signal that the user is actively interacting with the chat.
   * Called by ChatComponent on mouse, keyboard, touch, and scroll events.
   * Resets the idle timeout so the WebSocket stays open during active use.
   */
  notifyActivity(): void {
    this.lastActivityTime = Date.now();
  }

  /**
   * Signal that the browser tab visibility changed.
   * Hidden tabs use a shorter idle timeout (1 min vs 5 min) so Cloud Run
   * instances aren't held alive by backgrounded tabs.
   */
  notifyVisibilityChange(hidden: boolean): void {
    this.tabHidden = hidden;
    if (!hidden) {
      this.lastActivityTime = Date.now();
    }
  }

  /**
   * Starts the periodic keepalive ping and idle timeout check.
   * Both run on a single 30s interval to avoid extra timers.
   */
  private startKeepalive(): void {
    this.stopKeepalive();
    this.lastActivityTime = Date.now();
    this.keepaliveTimer = setInterval(() => {
      if (!this.ws$) return;

      if (!this.activeMessageObserver) {
        const timeout = this.tabHidden ? ChatService.IDLE_TIMEOUT_HIDDEN_MS : ChatService.IDLE_TIMEOUT_MS;
        if (Date.now() - this.lastActivityTime > timeout) {
          console.log(
            `[ChatService] Idle timeout (${timeout / 1000}s) — disconnecting to allow Cloud Run scale-to-zero`
          );
          this.disconnectIdle();
          return;
        }
      }

      try {
        this.ws$.next({ type: 'ping' });
      } catch (err) {
        console.warn('[ChatService] Keepalive ping failed, stopping timer:', err);
        this.stopKeepalive();
      }
    }, ChatService.KEEPALIVE_INTERVAL_MS);
  }

  private stopKeepalive(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = undefined;
    }
  }

  /**
   * Disconnect due to idle timeout. Uses a clean disconnect (no reconnect
   * attempts) since the user will transparently reconnect on next interaction.
   */
  private disconnectIdle(): void {
    if (this.activeMessageObserver) return;
    this.disconnect();
  }

  /** Queues a reconnection attempt with linear backoff. Only called on abnormal (1006) closures. */
  private scheduleReconnect(sessionId: string): void {
    this.reconnectAttempts++;
    const delay = ChatService.RECONNECT_DELAY_MS * this.reconnectAttempts;
    ClientLoggingService.log(
      'INFO',
      `[ChatService] Scheduling reconnect attempt ${this.reconnectAttempts}/${ChatService.MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (!this.manualDisconnect && !this.ws$ && !this.connectedSessionId) {
        this.preConnect(sessionId)
          .then(() => {
            // Issue #933: Notify subscribers if the disconnect interrupted an active stream
            if (this.hadActiveStreamOnDisconnect) {
              ClientLoggingService.log(
                'INFO',
                '[ChatService] Reconnected after mid-stream interruption, signalling retry'
              );
              this.hadActiveStreamOnDisconnect = false;
              this.reconnectedAfterInterrupt$.next();
            }
          })
          .catch(err => {
            ClientLoggingService.log('WARN', '[ChatService] Reconnect failed:', err);
          });
      }
    }, delay);
  }

  private cancelPendingReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  /**
   * Send a message and receive streaming response via persistent WebSocket (Issue #714).
   *
   * If a pre-connected WebSocket exists for this session, reuses it (near-zero overhead).
   * Otherwise, establishes a new connection transparently.
   */
  sendMessage(sessionId: string, request: SendMessageRequest): Observable<ChatMessageResponse> {
    return new Observable(observer => {
      (async () => {
        const payload = ChatService.buildPayload(request);

        try {
          this.lastActivityTime = Date.now();

          // Ensure WebSocket is connected (reuses pre-connected or creates new).
          // Always force a fresh connection if the current one is stale (different
          // session or missing ws$). preConnect() is idempotent for same-session.
          if (this.connectedSessionId !== sessionId || !this.ws$) {
            console.log('[ChatService] Establishing WebSocket before send');
            await this.preConnect(sessionId);
          } else if (this.connectionReady) {
            await this.connectionReady;
          }

          if (!this.ws$) {
            observer.error(new Error('WebSocket connection failed'));
            return;
          }

          ClientLoggingService.log('INFO', '[ChatService] Sending message via persistent WebSocket');
          this.activeMessageObserver = observer as Subscriber<ChatMessageResponse>;
          this.ws$.next(payload);
        } catch (error) {
          ClientLoggingService.log('ERROR', '[ChatService] WebSocket send failed, retrying once:', error);
          try {
            this.disconnect();
            // Brief delay lets Cloud Run proxy finish closing the old connection
            // before we open a new one (avoids rapid close/open race at the LB).
            await new Promise(resolve => setTimeout(resolve, 1000));
            await this.preConnect(sessionId);
            if (!this.ws$) {
              observer.error(new Error('WebSocket reconnect failed'));
              return;
            }
            this.activeMessageObserver = observer as Subscriber<ChatMessageResponse>;
            this.ws$.next(payload);
          } catch (retryError) {
            ClientLoggingService.log('ERROR', '[ChatService] WebSocket retry also failed:', retryError);
            observer.error(retryError);
          }
        }
      })();
    });
  }

  /**
   * Convert an unknown error value into an Error with a user-friendly message.
   * Handles CloseEvent (from WebSocket abnormal closures) and generic objects.
   */
  private static toError(err: unknown): Error {
    if (err instanceof Error) return err;
    if (typeof CloseEvent !== 'undefined' && err instanceof CloseEvent) {
      const ce = err as CloseEvent;
      return new Error(ce.wasClean ? 'Connection closed' : `Connection lost (code=${ce.code}). Please try again.`);
    }
    const msg = typeof err === 'string' ? err : 'An unexpected error occurred';
    return new Error(msg);
  }

  /**
   * Builds the JSON frame sent over the WebSocket for one chat turn.
   *
   * <p>Every field of {@link SendMessageRequest} that the backend needs must be copied here
   * explicitly. This constructs a fresh object rather than spreading the request, so a field that
   * exists on the request and is never copied is silently dropped at the transport boundary and
   * TypeScript reports nothing — the request property is simply unread. Image attachments were lost
   * that way: the composer set attachmentIds, the interface declared it, and no image ever reached
   * the model through this transport because this function did not carry it.
   *
   * <p>Adding a field to SendMessageRequest therefore means adding it here and asserting it in
   * chat.service.spec.ts, which tests this function's output rather than its caller.
   */
  private static buildPayload(request: SendMessageRequest): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      message: request.message,
      context: request.context,
      modelName: request.modelName,
      thinkingLevel: request.thinkingLevel,
    };
    if (request.thinkingBudget != null) {
      payload['thinkingBudget'] = request.thinkingBudget;
    }
    if (request.attachmentIds?.length) {
      payload['attachmentIds'] = request.attachmentIds;
    }
    return payload;
  }

  /**
   * Export a chat session as a branded PDF document (Issue #1074).
   * Calls the REST endpoint and triggers a browser file download.
   */
  exportSessionPdf(sessionId: string, projectName?: string, projectAddress?: string): Observable<void> {
    const projectId = this.env.getFirebaseConfig().projectId;
    const isDev = this.env.isDevelopment();

    // Determine the Cloud Function endpoints
    const url = isDev
      ? `http://127.0.0.1:5001/${projectId}/us-central1/exportSessionPdf`
      : `https://us-central1-${projectId}.cloudfunctions.net/exportSessionPdf`;

    return this.auth.user$.pipe(
      take(1),
      switchMap(user => {
        if (!user) throw new Error('Authentication required for PDF export');
        return from(user.getIdToken());
      }),
      switchMap(token => {
        const body: any = {
          sessionId,
          appUrl: window.location.origin,
        };
        if (projectName) {
          body.projectName = projectName;
        }
        if (projectAddress) {
          body.projectAddress = projectAddress;
        }
        if (token) {
          body.token = token;
        }

        // Direct Blob download using standard Angular HttpClient with POST
        return this.http.post(url, body, { responseType: 'blob' }).pipe(
          map(blob => {
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `PermitProof-Session-${sessionId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(downloadUrl);
          })
        );
      })
    );
  }

  /**
   * Delete a session.
   */
  deleteSession(sessionId: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE}/sessions/${sessionId}`);
  }

  private parseChunk(data: any): ChatMessageResponse {
    const chunk: ChatMessageResponse = data;

    if (chunk.toolCall) {
      if ((chunk.toolCall as any).parametersJson) {
        try {
          chunk.toolCall.parameters = JSON.parse((chunk.toolCall as any).parametersJson);
        } catch (e) {
          ClientLoggingService.log('WARN', '[ChatService] Failed to parse parametersJson:', e);
          chunk.toolCall.parameters = {};
        }
      }

      if ((chunk.toolCall as any).resultJson) {
        try {
          chunk.toolCall.result = JSON.parse((chunk.toolCall as any).resultJson);
        } catch (e) {
          ClientLoggingService.log('WARN', '[ChatService] Failed to parse resultJson:', e);
          chunk.toolCall.result = (chunk.toolCall as any).resultJson;
        }
      }

      this.interceptUiTools(chunk.toolCall);
    }

    ClientLoggingService.log('INFO', '[ChatService] Received chunk:', chunk);
    return chunk;
  }

  private interceptUiTools(toolCall: any): void {
    const toolName = toolCall.toolName || toolCall.name || '';

    if (
      toolName === 'ImageObjectSearchService_ShowObjectOnPage' ||
      toolName === 'show_object_on_page' ||
      toolName.includes('ShowObjectOnPage')
    ) {
      const result = toolCall.result;
      const url = result?.previewUrl || result?.preview_url;
      this.emitNavigationIfSafe('ShowObjectOnPage', url);
    }

    // Issue #909: ShowPage — navigate to a page/tab without highlight
    if (toolName === 'UiNavigationService_ShowPage' || toolName === 'show_page' || toolName.includes('ShowPage')) {
      const result = toolCall.result;
      const url = result?.navigationUrl || result?.navigation_url;
      this.emitNavigationIfSafe('ShowPage', url);
    }

    if (
      toolName === 'UiNavigationService_ShowTextOnPage' ||
      toolName === 'show_text_on_page' ||
      toolName.includes('ShowTextOnPage')
    ) {
      const result = toolCall.result;
      const url = result?.navigationUrl || result?.navigation_url;
      this.emitNavigationIfSafe('ShowTextOnPage', url);
    }
  }

  /** Validates a navigation URL and emits it to subscribers if safe. */
  private emitNavigationIfSafe(toolLabel: string, url: unknown): void {
    if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') && !url.includes('://')) {
      ClientLoggingService.log('INFO', `[ChatService] Intercepting ${toolLabel}, emitting navigation:`, url);
      this.navigationTrigger$.next(url);
    } else if (url) {
      ClientLoggingService.log('WARN', `[ChatService] ${toolLabel} result has unsafe or missing URL:`, url);
    }
  }
}
